import React, { useState, useEffect, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '@material-ui/core/Button';
import { MTableToolbar } from '@material-table/core';

import Background from 'components/Layout/Background';
import compose from 'helpers/compose';
import MaterialTable from 'components/Table/Table';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { AdvisorsStore } from 'stores/advisorsStore';
import useStyles from 'pages/Accounts/AccountsList/styles/Accounts';
import EditAdvisorModal from './EditAdvisorModal';
import GenerationDialog from 'components/GenerationDialog/GenerationDialog';

import AdvisorRow from './AdvisorRow';

interface Props {
  rootStore: RootStore;
}

const Advisors: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [store] = useState(new AdvisorsStore(rootStore));
  const { advisors, pageSize, onChangeRowsPerPage, downloadAccounts, fetchAdvisors, loading } = store;

  const [editDialog, setEditDialog] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'ACTIVATE' | 'REMOVE' | null>(null);
  const [finishedDialog, setFinishedDialog] = useState(false);
  const [advisor, setAdvisor] = useState({ realm: '', name: '' });

  useEffect(() => {
    fetchAdvisors();
  }, [fetchAdvisors]);

  const [filtersVisible, setFilterVisible] = useState(false);

  const toggleFilterVisibility = () => {
    setFilterVisible(!filtersVisible);
  };

  if (!advisors || loading) {
    return (
      <div className={classes.loadingContainer}>
        <LoadingIndicator size={56} />
      </div>
    );
  }

  return (
    <Background inside>
      <MaterialTable
        data={advisors}
        components={{
          Toolbar: (props) => (
            <div className={classes.toolbar}>
              <div className={classes.originalToolbar}>
                <MTableToolbar {...props} />
              </div>
              <div className={classes.buttons}>
                <Button variant="contained" color="primary" onClick={downloadAccounts}>
                  {t('accounts_download_csv')}
                </Button>
                <Button variant="contained" onClick={toggleFilterVisibility}>
                  {t('general_show_filters')}
                </Button>
              </div>
            </div>
          ),
          Row: (props) =>
            useMemo(
              () => (
                <AdvisorRow
                  store={store}
                  setAdvisor={setAdvisor}
                  setEditDialog={setEditDialog}
                  setConfirmDialog={setConfirmDialog}
                  setConfirmAction={setConfirmAction}
                  setFinishedDialog={setFinishedDialog}
                  {...props}
                />
              ),
              [props],
            ),
        }}
        columns={[
          {
            title: '',
            field: '',
            headerStyle: { width: 40, maxWidth: 40 },
            cellStyle: { width: 40, maxWidth: 40 },
          },
          {
            title: t('general_code'),
            field: 'code',
          },
          {
            title: t('general_username'),
            field: 'username',
          },
          {
            title: t('general_name'),
            field: 'name',
          },
          {
            title: t('backOffice_advisors_intermediaryCode'),
            field: 'intermediaryCode',
            customFilterAndSearch: (search, data) => {
              return t(`intermediary_${data.intermediaryCode}`).toLowerCase().indexOf(search.trim().toLowerCase()) > -1;
            },
            searchable: false,
          },
          {
            title: t('general_role'),
            field: 'role',
            searchable: false,
          },
          {
            title: t('backOffice_advisors_activeSince'),
            field: 'activeSince',
          },
          {
            title: '',
            field: '',
          },

          {
            title: t('table_actions'),
            field: '',
            customSort: (a, b) => {
              return b.reactivationEnabled - a.reactivationEnabled;
            },
          },
        ]}
        options={{
          searchFieldAlignment: 'left',
          headerStyle: {
            borderBottom: '2px solid rgb(224, 224, 224)',
          },
          pageSize,
          filtering: filtersVisible,
          sorting: true,
        }}
        onRowsPerPageChange={onChangeRowsPerPage}
      />

      <ConfirmDialog
        noHeader
        isOpen={confirmDialog}
        confirm={async () => {
          confirmAction === 'ACTIVATE' && (await store.activateAdvisor(advisor.realm, advisor.name));
          confirmAction === 'REMOVE' && (await store.removeAdvisor(advisor.realm, advisor.name));
          setConfirmDialog(false);
          fetchAdvisors();
        }}
        close={() => {
          setConfirmDialog(false);
        }}
      >
        {t(`backOffice_advisors_confirm_${confirmAction}`)} <strong>{advisor.name}</strong>?
      </ConfirmDialog>

      <ConfirmDialog noHeader isOpen={finishedDialog} confirm={() => setFinishedDialog(false)}>
        {t('backOffice_advisors_transferComplete')}
      </ConfirmDialog>

      <ConfirmDialog noHeader isOpen={store.cacheDialog} confirm={store.hideCacheDialog}>
        {t('backOffice_advisors_generatingCache')}
      </ConfirmDialog>

      <GenerationDialog store={store} />

      <EditAdvisorModal
        advisorsStore={store}
        advisor={advisor}
        opened={editDialog}
        handleClose={() => setEditDialog(false)}
        refresh={fetchAdvisors}
        setAdvisor={setAdvisor}
        setConfirmAction={setConfirmAction}
        setConfirmDialog={setConfirmDialog}
      />
    </Background>
  );
};
export default compose(inject('rootStore'), observer)(Advisors);
